import { Component, EventEmitter, Inject, Input, OnInit, Optional, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NbDialogRef } from '@nebular/theme';
import { finalize } from 'rxjs/operators';

import {
  CheckoutForUserPayload,
  CheckoutItem,
  CheckoutPayload,
  CheckoutResponse,
  CheckoutService,
} from '../checkout.service';
import { BankCardItem } from '../../../common/interfaces/card.interface';
import { CardsService } from '../../../common/services/card.service';
import { ErrorHandlerService } from '../../../common/services/error-handler.service';
import { MessageHandlerService } from '../../../common/services/message-handler.service';
import { TokenService } from '../../../common/services/token.service';
import { formatJalali } from '../../../common/utils/date';
import { convertToEnglishDigits } from '../../../common/utils/string';
import type { User } from '../../users/models/user.model';
import {
  getCheckoutStatusClass,
  getCheckoutStatusLabel,
} from '../checkout-status.util';

@Component({
  selector: 'ngx-checkout-modal',
  templateUrl: './checkout-modal.component.html',
  styleUrls: ['./checkout-modal.component.scss'],
})
export class CheckoutModalComponent implements OnInit {
  @Input() user?: User;
  @Input() pageMode = false;
  @Input() showHistory = true;
  @Output() requested = new EventEmitter<CheckoutResponse>();

  form!: FormGroup;
  loading = false;

  checkoutItems: CheckoutItem[] = [];
  checkoutListLoading = false;
  checkoutListLoadingMore = false;

  checkoutPage = 1;
  checkoutLimit = 10;
  checkoutHasMore = true;

  savedCards: BankCardItem[] = [];
  savedCardsLoading = false;
  cardsDropdownOpen = false;
  selectedCardId: string | null = null;
  private selectedSavedCardNumber = '';

  constructor(
    @Optional() @Inject(NbDialogRef) protected ref: NbDialogRef<CheckoutModalComponent> | null,
    private fb: FormBuilder,
    private checkoutService: CheckoutService,
    private cardsService: CardsService,
    private errorHandler: ErrorHandlerService,
    private messageHandler: MessageHandlerService,
    private tokenService: TokenService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      amount: [null, [Validators.required, Validators.min(1)]],
      description: ['', [Validators.maxLength(500)]],
      cardNumber: ['', [Validators.pattern(/^\d{15,19}$/)]],
      sheba: ['', [Validators.pattern(/^IR\d{24}$/i)]],
    });

    this.watchManualCardNumberChanges();

    if (!this.isForUser) {
      this.loadSavedCards();
      if (this.showHistory) this.loadCheckoutList();
    }
  }

  get isForUser(): boolean {
    return !!this.targetUserId;
  }

  get targetUserName(): string {
    const fullName = [
      this.user?.name || this.user?.firstName,
      this.user?.lastName,
    ]
      .filter(Boolean)
      .join(' ')
      .trim();

    return fullName || this.user?.phone || this.user?.email || 'کاربر انتخاب‌شده';
  }

  get amountControl() {
    return this.form.get('amount');
  }

  get cardNumberControl() {
    return this.form.get('cardNumber');
  }

  get shebaControl() {
    return this.form.get('sheba');
  }

  get descriptionControl() {
    return this.form.get('description');
  }

  loadSavedCards(): void {
    const user = this.tokenService.getUser<any>();
    const userId = user?._id || user?.id;

    if (!userId || this.savedCardsLoading) return;

    this.savedCardsLoading = true;

    this.cardsService
      .list({
        page: 1,
        limit: 50,
        filter: {
          user: {
            op: 'eq',
            val: userId,
          },
        },
        sort: { isPrimary: 'DESC', createdAt: 'DESC' },
      })
      .pipe(finalize(() => (this.savedCardsLoading = false)))
      .subscribe({
        next: (res) => {
          this.savedCards = res?.data?.items || [];
        },
        error: (error) => {
          this.errorHandler.handle(error);
        },
      });
  }

  openCardsDropdown(): void {
    if (this.isForUser) return;

    this.cardsDropdownOpen = true;

    if (!this.savedCards.length && !this.savedCardsLoading) {
      this.loadSavedCards();
    }
  }

  closeCardsDropdown(): void {
    this.cardsDropdownOpen = false;
  }

  onCardFieldFocusOut(event: FocusEvent): void {
    const currentTarget = event.currentTarget as HTMLElement;
    const nextTarget = event.relatedTarget as HTMLElement | null;

    if (nextTarget && currentTarget.contains(nextTarget)) return;

    setTimeout(() => {
      if (!currentTarget.contains(document.activeElement)) {
        this.closeCardsDropdown();
      }
    });
  }

  selectSavedCard(card: BankCardItem): void {
    const cardNumber = this.normalizeCardNumber(card?.cardNumber);

    if (!cardNumber) return;

    this.selectedCardId = this.getCardKey(card);
    this.selectedSavedCardNumber = cardNumber;

    this.form.patchValue({
      cardNumber,
      sheba: this.normalizeSheba(card?.sheba),
    });

    this.cardNumberControl?.markAsDirty();
    this.cardNumberControl?.markAsTouched();
    this.shebaControl?.markAsDirty();
    this.shebaControl?.markAsTouched();
    this.closeCardsDropdown();
  }

  isSavedCardSelected(card: BankCardItem): boolean {
    return (
      !!this.selectedCardId && this.selectedCardId === this.getCardKey(card)
    );
  }

  getCardKey(card: BankCardItem): string {
    return card?.id || card?._id || this.normalizeCardNumber(card?.cardNumber);
  }

  formatCardNumber(cardNumber: string): string {
    return this.normalizeCardNumber(cardNumber).replace(
      /(\d{4})(?=\d)/g,
      '$1 ',
    );
  }

  getStatusLabel(status: string): string {
    return getCheckoutStatusLabel(status);
  }

  getStatusClass(status: string): string {
    return getCheckoutStatusClass(status);
  }

  formatCheckoutDate(value?: string | Date): string {
    return value ? formatJalali(value, true) || '-' : '-';
  }

  loadCheckoutList(reset = true): void {
    if (reset) {
      this.checkoutPage = 1;
      this.checkoutHasMore = true;
      this.checkoutItems = [];
      this.checkoutListLoading = true;
    } else {
      if (!this.checkoutHasMore || this.checkoutListLoadingMore) return;
      this.checkoutListLoadingMore = true;
    }

    this.checkoutService
      .getCheckoutList(this.checkoutPage, this.checkoutLimit)
      .pipe(
        finalize(() => {
          this.checkoutListLoading = false;
          this.checkoutListLoadingMore = false;
        }),
      )
      .subscribe({
        next: (res) => {
          const newItems = res?.items || [];

          this.checkoutItems = reset
            ? newItems
            : [...this.checkoutItems, ...newItems];

          if (res?.total) {
            this.checkoutHasMore = this.checkoutItems.length < res.total;
          } else {
            this.checkoutHasMore = newItems.length === this.checkoutLimit;
          }

          if (this.checkoutHasMore) {
            this.checkoutPage += 1;
          }
        },
        error: (error) => {
          this.errorHandler.handle(error);
        },
      });
  }

  onCheckoutListScroll(event: Event): void {
    const element = event.target as HTMLElement;

    const threshold = 40;
    const position = element.scrollTop + element.clientHeight;
    const height = element.scrollHeight;

    if (height - position <= threshold) {
      this.loadCheckoutList(false);
    }
  }

  submit(): void {
    if (this.loading) return;

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading = true;

    const raw = this.form.getRawValue();

    const payload: CheckoutPayload = {
      amount: Number(raw.amount),
    };

    const description = String(raw.description || '').trim();
    const cardNumber = this.normalizeCardNumber(raw.cardNumber);
    const sheba = this.normalizeSheba(raw.sheba);

    if (description) payload.description = description;
    if (cardNumber) payload.cardNumber = cardNumber;
    if (sheba) payload.sheba = sheba;

    const request$ = this.isForUser
      ? this.checkoutService.checkoutForUser({
          ...payload,
          user: this.targetUserId,
        } as CheckoutForUserPayload)
      : this.checkoutService.checkout(payload);

    request$
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (res: CheckoutResponse) => {
          if (res?.redirectUrl) {
            window.location.href = res.redirectUrl;
            return;
          }

          if (this.isForUser) {
            this.messageHandler.handle(
              'برداشت برای کاربر',
              `درخواست برداشت برای ${this.targetUserName} با موفقیت ثبت شد.`,
            );
            this.ref?.close(res);
            return;
          }

          this.messageHandler.handle(
            'برداشت',
            'درخواست برداشت با موفقیت ثبت شد.',
          );

          this.form.reset();
          this.selectedCardId = null;
          this.selectedSavedCardNumber = '';
          this.closeCardsDropdown();
          if (this.showHistory) this.loadCheckoutList(true);
          this.requested.emit(res);
        },
        error: (error) => {
          this.errorHandler.handle(error, this.form);
        },
      });
  }

  close(): void {
    this.ref?.close();
  }

  openRequestsPage(): void {
    if (this.loading) return;
    this.close();
    this.router.navigate(['/pages/financial/withdrawals']);
  }

  private watchManualCardNumberChanges(): void {
    this.cardNumberControl?.valueChanges.subscribe((value) => {
      const cardNumber = this.normalizeCardNumber(value);

      if (this.selectedCardId && cardNumber !== this.selectedSavedCardNumber) {
        this.selectedCardId = null;
        this.selectedSavedCardNumber = '';
      }
    });
  }

  private normalizeCardNumber(value: string): string {
    return convertToEnglishDigits(value || '').replace(/\D/g, '');
  }

  private normalizeSheba(value: string): string {
    return convertToEnglishDigits(value || '')
      .replace(/\s|-/g, '')
      .toUpperCase();
  }

  private get targetUserId(): string {
    return this.user?.id || this.user?._id || '';
  }
}
